import { CropResidueType, UNIT_DISPLAY_LABEL } from "consts";

import { CROP_ROTATION, FERTILIZER_UNITS, PracticeLevel, PracticeSlug } from "models";

export type WithInjectedName<T> = T & { name: string };

export type PracticeCropRegionalValue = {
  seedingStart: string;
  seedingEnd: string;
  harvestStart: string;
  harvestEnd: string;
};

export type PracticeCrop = {
  id: string;
  enabled: boolean;
  harvestIsMandatory?: boolean;
  cropFamily: CROP_ROTATION | null;
  isCatchCrop: boolean;
  isGrassland: boolean;
  isLegume: boolean;
  isMainCrop: boolean;
  isPerennial: boolean;
  isUndersowing: boolean;
  isWinterCrop: boolean;
  neutral?: boolean;
  onlyVisibleToAdmins: boolean;
  residue: boolean;
  residueLeftByDefault: boolean;
  residueHidden: boolean;
  residueType: CropResidueType | null;
  synonyms: string[];
  recommendSplit: boolean;
  recommendSideCrop: boolean;
  sequestrationValues?: PracticeCropRegionalValue[];
  translationSlug: string;
  isModelReady?: boolean;
  isModelReadySoilPlus3?: boolean;
  isSilverModelReady?: boolean;
  imageURL?: string | null;
  cropBenchmarks?: CropBenchmarks;
  medianKwSow?: number | null;
  medianKwHarvest?: number | null;
};

export type CropBenchmarks = {
  harvestTonsPerHectare?: [number, number];
};

export type ExtendedCrop = WithInjectedName<PracticeCrop> & { isTopCrop?: boolean };

export type PracticeTillage = {
  id: string;
  dieselUsage: number;
  enabled: boolean;
  translationSlug: string;
};

export type PracticeSeeding = {
  id: string;
  dieselUsage: number;
  enabled: boolean;
  translationSlug: string;
};

export type PracticeFertilizerMineral = {
  id: string;
  enabled: boolean;
  translationSlug: string;
  nValue: number;
  hasUreaseInhibitor?: boolean;
  hasNitrificationInhibitor?: boolean;
  hasLowCarbonEmissions?: boolean;
};

export const FERTILIZER_ORGANIC_TYPE = {
  MANURE: "manure",
  DUNG: "dung",
} as const;

export type FertilizerOrganicType = (typeof FERTILIZER_ORGANIC_TYPE)[keyof typeof FERTILIZER_ORGANIC_TYPE];

export type PracticeFertilizerOrganic = {
  id: string;
  enabled: boolean;
  translationSlug: string;
  nValue: number;
  mineralEquivalent: number;
  isModelReady?: boolean;
  /** Solid (manure) vs liquid (dung) — decides which amount unit the form pre-selects. */
  type?: FertilizerOrganicType;
};

export const ORGANIC_AMOUNT_UNITS = [FERTILIZER_UNITS.CUBIC_METERS, FERTILIZER_UNITS.TONNES] as const;

export type OrganicAmountUnit = (typeof ORGANIC_AMOUNT_UNITS)[number];

export const resolveNValueUnitTypeFromOrganicFertAmountUnit = (unit: OrganicAmountUnit | null | undefined): null | keyof typeof UNIT_DISPLAY_LABEL =>
  unit ? (unit === FERTILIZER_UNITS.TONNES ? "kilograms_N_per_tonne" : "kilograms_N_per_cubic_meter") : null;

export type PracticeFertilizerApplication = {
  id: string;
  dungMultiplier: number;
  manureMultiplier: number;
  enabled: boolean;
  translationSlug: string;
};

export type PracticePlantProtection = {
  id: string;
  dieselUsage: number | null;
  enabled: boolean;
  translationSlug: string;
};

export type PracticeLandUseChange = {
  id: string;
  translationSlug: string;
};

export interface PracticeSpecification {
  readonly id: string;
  readonly slug: PracticeSlug;
  readonly enabled: boolean;
  readonly level: PracticeLevel;
}
